/**
 * EHS management MIS API client (/api/reports/ehs-mis/*).
 *
 * Separate from ehsMisApi.js, which serves the workbook-parity grid. Auth is
 * injected by the global axios interceptor in App.js.
 */
import axios from 'axios';

const base = () => `${process.env.REACT_APP_BACKEND_URL}/api/reports/ehs-mis`;

/** Section catalog: titles, availability and limitations. No values. */
export async function fetchSectionCatalog({ signal } = {}) {
  const { data } = await axios.get(`${base()}/section-catalog`, { signal });
  return data?.sections || [];
}

/**
 * Computed management MIS.
 * params: { siteId?, preset?, dateFrom?, dateTo?, sections?, standard? }
 * Returns { report_id, site_id, range, standard, template, sections, details }.
 */
export async function fetchMisSections(
  { siteId, preset, dateFrom, dateTo, sections, standard } = {}, { signal } = {},
) {
  const params = {};
  if (siteId) params.site_id = siteId;
  if (preset) params.preset = preset;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (sections) params.sections = sections;
  if (standard) params.standard = standard;
  const { data } = await axios.get(`${base()}/sections`, { params, signal });
  return data;
}

/**
 * File extension per format. 'presentation' is a landscape PDF (a finished
 * document); 'pptx' is the same deck as an editable PowerPoint file.
 */
const EXTENSIONS = { excel: 'xlsx', presentation: 'pdf', pptx: 'pptx',
                     pdf: 'pdf', csv: 'csv' };

/**
 * Download the management report. Returns { blob, filename }.
 *
 * `format` is one of pdf | excel | csv | presentation | pptx. The last two are the
 * executive slide deck — the same computed figures as the detailed PDF, arranged
 * as a scorecard, ranked findings and one theme per slide — as a fixed PDF and as
 * an editable PowerPoint file respectively.
 */
export async function downloadMisReport(
  { siteId, preset, dateFrom, dateTo, sections, standard, format },
) {
  const params = { format };
  if (siteId) params.site_id = siteId;
  if (preset) params.preset = preset;
  if (dateFrom) params.date_from = dateFrom;
  if (dateTo) params.date_to = dateTo;
  if (sections) params.sections = sections;
  if (standard) params.standard = standard;
  const resp = await axios.get(`${base()}/report`, { params, responseType: 'blob' });
  const cd = resp.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(cd);
  const stem = (format === 'presentation' || format === 'pptx')
    ? 'ehs_performance_review' : 'ehs_mis_report';
  const ext = EXTENSIONS[format] || format;
  return { blob: resp.data, filename: match ? match[1] : `${stem}.${ext}` };
}

/** Report generation history for the scope. */
export async function fetchReportHistory({ siteId, limit = 20 } = {}, { signal } = {}) {
  const params = { limit };
  if (siteId) params.site_id = siteId;
  const { data } = await axios.get(`${base()}/reports`, { params, signal });
  return data?.reports || [];
}
